import RepartitionData from "@/domain/entities/dashboard/RepartitionData";
import { eventKeys } from "@/lib/eventKeys";
import { queryKeys } from "@/lib/queryKeys";
import navbarDataToRepartitionDataMapper from "@/mappers/repartitionDataMapper";
import {
    NavBarContent as NavBarContentSDK,
    useSdk,
} from "@cs_teamwaydev/mailvista-sdk_react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import useGetEventBus from "../useGetEventBus";
import { useToast } from "../utils/useToast";

const useGetDashboardRepartition = () => {
    const { mailManager } = useSdk();
    const { toast } = useToast();
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    const fetchRepartitionData = async (): Promise<
        RepartitionData[] | undefined
    > => {
        try {
            const navBarData = await mailManager.getNavBarContent();
            if (!navBarData) {
                throw new Error("Navbar data is undefined");
            }

            return navbarDataToRepartitionDataMapper(navBarData);
        } catch (error) {
            toast({
                title: t("errors.navbar"),
                description: t("errors.global.description"),
                variant: "error",
            });

            console.error(error);

            return undefined;
        }
    };

    const { data: repartitionData, isLoading } = useQuery({
        queryKey: queryKeys.dashboardRepartition(),
        queryFn: fetchRepartitionData,
    });

    // Apply navigation updates immediately, exactly like the sidebar navbar
    // (useGetNavbarData). The previous 6s debounce made this widget lag several seconds
    // behind the navbar and the welcome message (AB#1710). The flicker ("juggling") the
    // debounce was meant to prevent is now handled where it belongs:
    //   - at the source: the SDK de-duplicates identical `navigation-updated` events;
    //   - at the render: the BubbleChart morphs bubbles instead of rebuilding from scratch.
    // So no debounce is needed here anymore.
    const handleNavigationUpdated = async (newNavBarData: NavBarContentSDK) => {
        await queryClient.cancelQueries({
            queryKey: queryKeys.dashboardRepartition(),
        });
        queryClient.setQueryData(
            queryKeys.dashboardRepartition(),
            navbarDataToRepartitionDataMapper(newNavBarData),
        );
    };

    const { off: offNavigationUpdated } = useGetEventBus<NavBarContentSDK>(
        eventKeys.navigation.updated,
        handleNavigationUpdated,
    );

    useEffect(
        () => () => {
            offNavigationUpdated(
                eventKeys.navigation.updated,
                handleNavigationUpdated,
            );
        },
        [offNavigationUpdated],
    );

    return {
        data: repartitionData,
        isLoading,
    };
};

export default useGetDashboardRepartition;
